/**
 * Tooltip — headless primitive wrapping Radix Tooltip.
 *
 * Radix's Tooltip owns the hard parts: hover + focus triggers with
 * configurable delay, `aria-describedby` wiring, keyboard reachability,
 * pointer-vs-touch heuristics (ARCHITECTURE §6). We wrap thinly:
 *   - `Tooltip.Provider` surfaces the default delay contract
 *   - Permission gating on <Root> (canView=false → primitive returns null)
 *   - `data-oshon-primitive="tooltip-*"` styling hooks
 *
 * Deliberately no audit events. Tooltips open/close on every hover and
 * an audit stream would be noise, not signal — principle #3 ("RBAC +
 * audit by default") covers actions, not purely informational surfaces.
 * See `docs/adr/primitives/003-tooltip-radix-wrap.md`.
 */

'use client';

import * as RadixTooltip from '@radix-ui/react-tooltip';
import { forwardRef } from 'react';
import type {
  ComponentPropsWithoutRef,
  ElementRef,
  ReactNode,
} from 'react';
import {
  evaluatePermission,
  resolvePermissions,
  usePermissions,
  useAudit,
  type PermissionContext,
} from '../context.js';
import { CANONICAL_ACTIONS } from '../actions.js';

// ────────────────────────────────────────────────────────────────────────────
// Provider

export type TooltipProviderProps = ComponentPropsWithoutRef<typeof RadixTooltip.Provider>;

/**
 * Configures default delay + skip-delay behavior for every descendant
 * Tooltip. Mount once near the app root.
 */
function Provider(props: TooltipProviderProps) {
  return <RadixTooltip.Provider {...props} />;
}
Provider.displayName = 'Tooltip.Provider';

// ────────────────────────────────────────────────────────────────────────────
// Root

export interface TooltipRootProps extends RadixTooltip.TooltipProps {
  /**
   * Per-instance permission override. Merges over the ambient
   * `PermissionContext`. A tooltip has no useful non-interactive visible
   * state, so any denied permission (regardless of `mode`) hides both
   * trigger and content and emits a `permission:denied` audit event.
   */
  permissions?: Partial<PermissionContext>;
  /**
   * Resource name passed to `permissions.can('view', resource, attrs)`.
   * Default `'tooltip'`. Override for specific semantics like
   * `resource="field:ssn-help"`.
   */
  resource?: string;
  /** Attribute bag for attribute-based access control. */
  permissionAttrs?: Record<string, unknown>;
  children: ReactNode;
}

function Root({
  children,
  permissions,
  resource = 'tooltip',
  permissionAttrs,
  ...rest
}: TooltipRootProps) {
  const context = usePermissions();
  const effective = resolvePermissions(permissions, context);
  const emit = useAudit();

  const { granted, denyReason } = evaluatePermission(
    effective,
    'view',
    resource,
    permissionAttrs,
  );

  if (!granted) {
    // Tooltips have no meaningful visible-but-inert state — all deny
    // modes hide. We still emit the denial so governance can surface
    // over-permissive help-text surfaces (principle #3, ADR-003).
    emit({
      component: 'tooltip',
      action: CANONICAL_ACTIONS.PERMISSION_DENIED,
      permissionResult: 'denied',
      ...(denyReason ? { denyReason } : {}),
      details: { attemptedAction: 'view', resource },
    });
    return null;
  }

  return <RadixTooltip.Root {...rest}>{children}</RadixTooltip.Root>;
}
Root.displayName = 'Tooltip.Root';

// ────────────────────────────────────────────────────────────────────────────
// Trigger

export type TooltipTriggerProps = ComponentPropsWithoutRef<typeof RadixTooltip.Trigger>;

const Trigger = forwardRef<
  ElementRef<typeof RadixTooltip.Trigger>,
  TooltipTriggerProps
>(function TooltipTrigger(props, ref) {
  return (
    <RadixTooltip.Trigger
      ref={ref}
      data-oshon-primitive="tooltip-trigger"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Portal

export type TooltipPortalProps = RadixTooltip.TooltipPortalProps;

function Portal(props: TooltipPortalProps) {
  return <RadixTooltip.Portal {...props} />;
}
Portal.displayName = 'Tooltip.Portal';

// ────────────────────────────────────────────────────────────────────────────
// Content

export type TooltipContentProps = ComponentPropsWithoutRef<typeof RadixTooltip.Content>;

/**
 * Content. Radix handles `role="tooltip"`, positioning, and collision
 * detection. We add the styling hook.
 */
const Content = forwardRef<
  ElementRef<typeof RadixTooltip.Content>,
  TooltipContentProps
>(function TooltipContent(props, ref) {
  return (
    <RadixTooltip.Content
      ref={ref}
      data-oshon-primitive="tooltip-content"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Arrow

export type TooltipArrowProps = ComponentPropsWithoutRef<typeof RadixTooltip.Arrow>;

const Arrow = forwardRef<
  ElementRef<typeof RadixTooltip.Arrow>,
  TooltipArrowProps
>(function TooltipArrow(props, ref) {
  return (
    <RadixTooltip.Arrow
      ref={ref}
      data-oshon-primitive="tooltip-arrow"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Compound export

/**
 * Compound Tooltip primitive. Use as:
 *
 *   <Tooltip.Provider>
 *     <Tooltip.Root>
 *       <Tooltip.Trigger asChild>
 *         <button>Help</button>
 *       </Tooltip.Trigger>
 *       <Tooltip.Portal>
 *         <Tooltip.Content>Inline documentation.</Tooltip.Content>
 *       </Tooltip.Portal>
 *     </Tooltip.Root>
 *   </Tooltip.Provider>
 */
export const Tooltip = {
  Provider,
  Root,
  Trigger,
  Portal,
  Content,
  Arrow,
};

export type TooltipProps = TooltipRootProps;
